import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calculator, Compass, HardHat, PenTool, Users, TrendingUp, Brain, BarChart3, FileText, UserCheck } from 'lucide-react';

const faqData = [
  {
    question: "What professional service firms benefit most from automation?",
    answer: "Architecture practices, engineering consultancies, surveying firms, accountants, quantity surveyors, and management consultants see the greatest returns. Any firm with coordination overhead, document-heavy processes, or repetitive client admin benefits significantly."
  },
  {
    question: "Will automation replace our professional staff?",
    answer: "No. Automation handles repetitive admin so your professionals can focus on billable, high-value work. Human oversight is built into every system."
  },
  {
    question: "How do you handle sensitive client data?",
    answer: "We follow the SAFE-NZ framework and comply with the NZ Privacy Act 2020. Client data is never used to train AI models. Role-based access controls and encryption are standard."
  },
  {
    question: "How long until we see results?",
    answer: "Most firms see measurable time savings within 2–4 weeks of initial deployment, with structured reviews at 30, 60, and 90 days to track ROI against baseline."
  },
  {
    question: "Can automation integrate with our existing software?",
    answer: "Yes. We integrate with industry tools including Xero, Abtrac, project management platforms, document management systems, CRMs, and specialist practice software."
  },
  {
    question: "What does the discovery process involve?",
    answer: "A structured workshop mapping your current workflows, identifying bottlenecks, and capturing baseline metrics. You receive a prioritised roadmap with ROI projections before any build commitment."
  },
  {
    question: "How can automation improve proposal accuracy?",
    answer: "By connecting historical project data with past fee proposals, automation systems can identify comparable past engagements and recommend fee ranges with confidence levels — reducing reliance on personal memory and improving consistency."
  },
  {
    question: "Can workflow automation support architectural and engineering firms?",
    answer: "Yes. Workflow automation is particularly effective for architecture and engineering firms where proposal preparation, consultant coordination, and project staffing create significant administrative overhead."
  },
  {
    question: "Does automation replace professional judgement?",
    answer: "No. Automation provides structured reference points and recommendations. Senior staff retain full oversight and decision-making authority. The system learns from expert adjustments to improve over time."
  }
];

const industryExamples = [
  { icon: <PenTool size={24} />, title: "Architects", desc: "Automate scope documentation, consultant coordination, project status reporting, fee proposal preparation, and client onboarding workflows." },
  { icon: <HardHat size={24} />, title: "Engineers", desc: "Streamline RFI management, compliance tracking, technical document collection, proposal automation, and project milestone reporting." },
  { icon: <Compass size={24} />, title: "Surveyors & QS", desc: "Automate data collection workflows, client communication, fee estimation, report generation, and scheduling." },
  { icon: <Calculator size={24} />, title: "Accountants", desc: "Reduce manual data entry, automate document requests, streamline client onboarding, and generate compliance reports." },
  { icon: <Users size={24} />, title: "Consultants & Advisors", desc: "Automate proposal workflows, client reporting, knowledge management, resource allocation, and follow-up sequences." }
];

const AIAutomationProfessionalServices: React.FC = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI & Workflow Automation for Professional Service Firms",
    "provider": { "@type": "Organization", "name": "ThinkTomorrow.AI" },
    "description": "Workflow automation consulting for architecture, engineering, surveying, accounting, and consulting firms in New Zealand. Includes fee proposal intelligence and staff allocation automation.",
    "areaServed": { "@type": "Country", "name": "New Zealand" },
    "serviceType": "Workflow Automation Consulting"
  };

  return (
    <>
      <Helmet>
        <title>AI Automation for Professional Services | ThinkTomorrow.AI</title>
        <meta name="description" content="Workflow automation for architects, engineers, surveyors, accountants and consultants. Fee proposal intelligence, staff allocation automation, and project workflow automation for professional service firms." />
        <link rel="canonical" href="https://www.thinktomorrow.ai/ai-automation-professional-services" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <PageHeader
          title="AI & Workflow Automation for Professional Service Firms"
          subtitle="Reduce admin overhead, improve proposal accuracy, and protect margins with responsible automation."
        />

        {/* Introduction / Definition Block */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="p-6 bg-muted/30 rounded-lg border-l-4 border-brand-teal">
              <p className="text-lg text-foreground">
                <strong>Workflow automation for professional services</strong> replaces repetitive admin tasks — fee proposal preparation, document requests, client onboarding, status reporting, consultant coordination — with reliable systems that run consistently. The result: fewer errors, faster turnaround, improved proposal accuracy, and more time spent on billable, high-value work.
              </p>
              <p className="text-muted-foreground mt-4">
                This applies to architects, engineers, quantity surveyors, surveyors, planning consultants, resource consent specialists, and multi-disciplinary consulting firms across New Zealand.
              </p>
            </div>
          </div>
        </section>

        {/* Common Operational Bottlenecks */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Common Operational Bottlenecks</h2>
            <div className="space-y-4">
              {[
                { title: "Scope creep from unclear processes", desc: "Without standardised intake and scoping workflows, projects expand beyond original agreements — eroding profitability on every job." },
                { title: "Duplicated admin across team members", desc: "Multiple people chasing the same documents, updating the same records, or preparing the same reports independently." },
                { title: "Coordination overhead", desc: "Managing sub-consultants, contractors, and client communications manually creates bottlenecks and delays that compound across projects." },
                { title: "Manual fee proposal preparation", desc: "Partners and Heads of Department spending hours reviewing past projects to determine scope, staffing, and fee structures — often relying on personal memory rather than structured data." },
                { title: "Inconsistent staffing decisions", desc: "Allocating staff to projects without clear visibility of current workloads, experience levels, or departmental requirements across the organisation." }
              ].map((item, i) => (
                <div key={i} className="p-5 bg-background rounded-lg border">
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Workflow Automation Opportunities */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Where Automation Helps</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Client onboarding & intake forms",
                "Document requests & collection",
                "Scope drafting assistance",
                "Consultant coordination & updates",
                "Reporting automation",
                "Knowledge assistants for internal teams",
                "Fee proposal preparation & benchmarking",
                "Staff allocation & workload balancing"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border">
                  <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={20} />
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fee Proposal Intelligence System */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand-teal/10 rounded-lg">
                <FileText className="text-brand-teal" size={28} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Fee Proposal Intelligence for Professional Service Firms</h2>
            </div>

            <div className="space-y-6 text-foreground">
              <p>
                Preparing accurate fee proposals is one of the most time-consuming responsibilities for senior staff in professional service firms. In many organisations, partners and Heads of Department must manually review previous projects to determine appropriate scope, staffing levels, and fee structures.
              </p>
              <p className="text-muted-foreground">
                This process is slow, inconsistent, and often relies heavily on personal memory rather than structured historical data.
              </p>
              <p>
                Workflow automation systems can improve this process by connecting historical project data with past fee proposals.
              </p>

              <div className="p-6 bg-background rounded-lg border">
                <h3 className="font-semibold text-foreground mb-4">Integration with Project Management Systems</h3>
                <p className="text-muted-foreground mb-4">
                  Using integration with project management systems such as Abtrac, the system can reference completed projects including:
                </p>
                <ul className="space-y-2">
                  {["Departmental involvement", "Historical project costs", "Staffing structures", "Project scale and complexity"].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p>
                Fee proposals stored separately (for example within document repositories such as Dropbox) are then linked to these completed jobs.
              </p>

              <div className="p-6 bg-background rounded-lg border">
                <h3 className="font-semibold text-foreground mb-4">Comparable Project Analysis</h3>
                <p className="text-muted-foreground mb-4">
                  When a new proposal is prepared, the system analyses historical projects and identifies the most comparable past engagements based on factors such as:
                </p>
                <ul className="space-y-2">
                  {["Departmental involvement", "Cost structures", "Project type", "Scale of work"].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-brand-teal/5 to-brand-purple/5 rounded-lg border border-brand-teal/20">
                <h3 className="font-semibold text-foreground mb-4">System Output</h3>
                <p className="text-muted-foreground mb-4">The system then provides:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: <TrendingUp size={20} />, label: "Recommended fee range" },
                    { icon: <BarChart3 size={20} />, label: "Confidence level based on historical similarity" },
                    { icon: <FileText size={20} />, label: "References to comparable completed projects" }
                  ].map((item, i) => (
                    <div key={i} className="flex flex-col items-center text-center p-4 bg-background rounded-lg">
                      <div className="text-brand-teal mb-2">{item.icon}</div>
                      <span className="text-sm text-foreground font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p>
                These comparable projects can be reviewed directly to assess relevance and ensure the recommendation aligns with the current project.
              </p>
              <p className="text-muted-foreground italic">
                The goal is not to replace professional judgement but to provide structured reference points so senior staff do not need to start every proposal from scratch.
              </p>
            </div>
          </div>
        </section>

        {/* Staff Allocation Automation */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand-teal/10 rounded-lg">
                <UserCheck className="text-brand-teal" size={28} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Automating Project Staffing Decisions</h2>
            </div>

            <div className="space-y-6 text-foreground">
              <p>
                Once a proposal is prepared and accepted, the next operational challenge is allocating the right staff to the work.
              </p>
              <p>
                In many professional service firms, this responsibility falls on Heads of Department who must manually balance:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Staff availability",
                  "Experience levels",
                  "Project complexity",
                  "Competing workloads"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border">
                    <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-muted-foreground">
                This process can become a significant administrative burden, particularly in firms managing dozens of concurrent projects.
              </p>
              <p>
                Workflow automation systems can assist by incorporating staff availability and experience data when a project moves from proposal to delivery.
              </p>

              <div className="p-6 bg-muted/30 rounded-lg border">
                <h3 className="font-semibold text-foreground mb-4">Automated Staff Suggestions</h3>
                <p className="text-muted-foreground mb-4">
                  Using the department structure of the project, the system can suggest suitable staff allocations based on:
                </p>
                <ul className="space-y-2">
                  {["Departmental requirements", "Current workload", "Relevant experience"].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p>
                Department heads retain full oversight and can adjust the recommended allocation where necessary. This significantly reduces coordination time while still ensuring that experienced staff remain in control of project delivery decisions.
              </p>
            </div>
          </div>
        </section>

        {/* Workflow Intelligence and Continuous Learning */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand-teal/10 rounded-lg">
                <Brain className="text-brand-teal" size={28} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Continuous Learning Through Human-in-the-Loop Feedback</h2>
            </div>

            <div className="space-y-6 text-foreground">
              <p>
                A key element of the system is its human-in-the-loop design.
              </p>
              <p>
                While the system provides recommendations, department heads remain responsible for approving or adjusting proposed fee levels. If a recommendation is changed, the system records the reason for the adjustment. This creates a structured feedback loop that allows the system to learn from expert judgement over time.
              </p>

              <div className="p-6 bg-background rounded-lg border">
                <h3 className="font-semibold text-foreground mb-4">Common Adjustment Reasons</h3>
                <p className="text-muted-foreground mb-4">For example, adjustments may occur because of:</p>
                <ul className="space-y-2">
                  {[
                    "Complex site conditions",
                    "Unusual regulatory requirements",
                    "Client-specific considerations",
                    "Anticipated design revisions"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p>
                Capturing this reasoning allows the system to gradually build a knowledge base of expert insights that improves the consistency of future proposals.
              </p>
              <p className="text-muted-foreground italic">
                Rather than replacing professional expertise, the system strengthens organisational knowledge and preserves valuable operational insights.
              </p>
            </div>
          </div>
        </section>

        {/* Case Example */}
        <section className="py-16 bg-gradient-to-br from-[#0B1939] to-[#081631] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Case Example: Improving Proposal Accuracy and Coordination</h2>
            <div className="space-y-6">
              <p className="text-gray-300">
                In one professional services environment with more than 10,000 completed projects, the workflow system was designed to support both proposal preparation and project staffing.
              </p>

              <div className="p-6 bg-white/5 rounded-lg border border-white/10">
                <h3 className="font-semibold text-white mb-4">System Integration</h3>
                <ul className="space-y-2">
                  {[
                    "Historical job data from the project management system",
                    "Past fee proposals stored in document repositories",
                    "Departmental project structures"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-white/5 rounded-lg border border-white/10">
                <h3 className="font-semibold text-white mb-4">Proposal Workflow Output</h3>
                <p className="text-gray-300 mb-4">When preparing a new proposal, the system analyses comparable past projects and provides:</p>
                <ul className="space-y-2">
                  {[
                    "A recommended fee level",
                    "A confidence score",
                    "Three similar completed projects for review"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-300">
                Department heads can review these examples before finalising the proposal. Once the project proceeds, the system can also assist with staff allocation by suggesting team members based on availability and experience.
              </p>
              <p className="text-gray-400 italic">
                This approach reduces the time required to prepare proposals while improving consistency and visibility across departments.
              </p>

              <Link to="/case-studies/architect-workflow-intelligence">
                <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-brand-navy flex items-center gap-2 mt-4">
                  Read the Full Architecture Case Study <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Future Operational Insights */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-brand-teal/10 rounded-lg">
                <BarChart3 className="text-brand-teal" size={28} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">From Automation to Operational Insight</h2>
            </div>

            <div className="space-y-6 text-foreground">
              <p>
                As organisations accumulate more structured project data, systems like this can also begin to identify broader operational patterns.
              </p>

              <div className="p-6 bg-muted/30 rounded-lg border">
                <h3 className="font-semibold text-foreground mb-4">Margin Leakage Visibility</h3>
                <p className="text-muted-foreground mb-4">For example, firms may eventually be able to visualise:</p>
                <ul className="space-y-2">
                  {[
                    "Where projects consistently exceed estimated effort",
                    "Which project types experience scope expansion",
                    "Where particular departments experience margin pressure"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <TrendingUp className="text-brand-teal mt-0.5 shrink-0" size={18} />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p>
                These insights can help leadership teams identify areas where processes, pricing models, or staffing approaches may need adjustment.
              </p>
              <p className="text-muted-foreground italic">
                The result is not only faster workflows, but clearer visibility into how projects perform across the business.
              </p>
            </div>
          </div>
        </section>

        {/* Industry Examples */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Industry Examples</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {industryExamples.map((ex, i) => (
                <div key={i} className="p-6 bg-background rounded-lg border">
                  <div className="text-brand-teal mb-3">{ex.icon}</div>
                  <h3 className="font-semibold text-foreground mb-2">{ex.title}</h3>
                  <p className="text-muted-foreground text-sm">{ex.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Method */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Our Implementation Method</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { num: "1", title: "Discovery", desc: "Map workflows, identify bottlenecks, capture baseline metrics." },
                { num: "2", title: "Roadmap", desc: "Prioritise by ROI, design governance and oversight." },
                { num: "3", title: "Implementation", desc: "Build, integrate, train, deploy with rollback plans." },
                { num: "4", title: "Optimisation", desc: "30/60/90 day reviews, continuous improvement." }
              ].map((step, i) => (
                <div key={i} className="text-center p-6 bg-muted/30 rounded-lg border">
                  <div className="w-10 h-10 rounded-full bg-brand-teal text-white flex items-center justify-center font-bold mx-auto mb-3">{step.num}</div>
                  <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#0B1939] to-[#081631] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Streamline Your Project Workflows?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Book a workflow discovery session to explore how automation can reduce administrative work while improving visibility across your projects.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="primary" className="flex items-center gap-2">
                  Book Discovery Session <ArrowRight size={16} />
                </Button>
              </Link>
              <Link to="/ai-workflow-automation-nz">
                <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-brand-navy flex items-center gap-2">
                  Explore Workflow Automation <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <h3 className="font-semibold text-foreground mb-4">Related Pages</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/workflow-automation-marlborough" className="text-brand-teal hover:underline text-sm">Workflow Automation Marlborough →</Link>
              <Link to="/ai-workflow-automation-nz" className="text-brand-teal hover:underline text-sm">AI Workflow Automation NZ →</Link>
              <Link to="/case-studies/architect-workflow-intelligence" className="text-brand-teal hover:underline text-sm">Case Study: Architect Workflow Intelligence →</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AIAutomationProfessionalServices;
